import * as React from 'react'
import { css } from '@emotion/core'
import { Container, Box, Grid } from '@material-ui/core'
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { Theme } from '~/styles'
import { Heading, Typography, Input, Button, Main, Header } from '~/components'
import { handleChange, handleBlur } from '~/utils/formik'
import { memberIdSchema } from '~/utils'
import { styled } from '@mui/material/styles'
import { colors } from '~/styles/color'

interface MemberIdFormValues {
  memberId: string
}

interface MemberIdCollectionUIProps {
  onSubmit: (memberId: string) => void | Promise<void>
  isHealthPlanMember: boolean
  isLoading?: boolean
  error?: string | null
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.navy[500],
    color: colors.white,
    maxWidth: 500,
    fontSize: theme.typography.pxToRem(24),
    fontFamily: 'Source Sans Pro',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    padding: 5,
    margin: 20,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: colors.navy[500],
    position: 'relative',
  },
}))

/**
 * Reusable UI component for the Member ID input screen.
 * Used by both authenticated and unauthenticated member id flows.
 */
export const MemberIdCollectionUI: React.FC<MemberIdCollectionUIProps> = ({
  onSubmit,
  isHealthPlanMember,
  isLoading = false,
  error,
}) => {
  const [open, setOpen] = React.useState(false)

  const handleTooltipClose = () => {
    setOpen(false)
  }

  const handleTooltipOpen = () => {
    setOpen(true)
  }

  const validationSchema = Yup.object().shape({
    memberId: memberIdSchema,
  })
  const initialState: MemberIdFormValues = { memberId: '' }

  return (
    <>
      <Header />
      <Container>
        <Box mt={10}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={10} md={5}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={12}>
                  <Main px={{ xs: 0 }}>
                    <>
                      {!isHealthPlanMember ? (
                        <Heading>Enter your health insurance Member ID number</Heading>
                      ) : (
                        <Heading>Enter your Firefly health insurance Member ID number</Heading>
                      )}
                    </>
                    <Typography variant="subText">
                      Type all numbers, letters, and characters exactly as they appear on your
                      insurance card.
                    </Typography>
                    <HtmlTooltip
                      onClose={handleTooltipClose}
                      open={open}
                      PopperProps={{
                        disablePortal: true,
                      }}
                      title={
                        <Box flex={1} flexDirection="column" p={3}>
                          {!isHealthPlanMember ? (
                            <>
                              <Typography css={styles.toolTipHeading}>
                                Finding your Member ID number
                              </Typography>
                              <Typography css={styles.toolTipContent}>
                                Your Member ID is typically a 9-12 character mix of letters and
                                numbers on the front of your insurance card, often labeled as
                                “Member ID”, “ID Number”, “Subscriber ID”, or “ID #”.
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography css={styles.toolTipHeading}>
                                Finding your Member ID number
                              </Typography>
                              <Typography css={styles.toolTipContent}>
                                Your Firefly Member ID is a combination of letters and numbers on
                                the front of your firefly Health insurance card.{'\n \n'}
                                If you&apos;ve lost or not yet received your Firefly Health
                                insurance card, please call us at{' '}
                                <a href="tel:888-897-1887" title="Call us" css={styles.paragraph}>
                                  (888) 897-1887
                                </a>{' '}
                                between 7am and 7pm EST and we&apos;ll be happy to assist you.
                              </Typography>
                            </>
                          )}
                          <Box
                            display="flex"
                            flex={1}
                            flexDirection="column"
                            alignItems="flex-end"
                            mt={3}
                          >
                            <Button css={styles.toolTipButton} onClick={handleTooltipClose}>
                              Got it
                            </Button>
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box css={styles.toolTipChildren}>
                        <Typography
                          variant="subText"
                          style={{
                            textDecoration: 'underline',
                          }}
                          pt={5}
                          onClick={handleTooltipOpen}
                        >
                          Where can I find this info?
                        </Typography>
                      </Box>
                    </HtmlTooltip>

                    <Formik<MemberIdFormValues>
                      validateOnChange={false}
                      initialValues={initialState}
                      onSubmit={async values => {
                        await onSubmit(values.memberId)
                      }}
                      validationSchema={validationSchema}
                    >
                      {formik => {
                        return (
                          <form onSubmit={formik.handleSubmit} css={styles.wrapper}>
                            <Box my={2}>
                              <Input
                                label="Member ID number"
                                name="memberId"
                                required
                                maxLength={20}
                                value={formik.values.memberId || ''}
                                error={
                                  error ||
                                  (formik.touched.memberId ? formik.errors.memberId : void 0)
                                }
                                onChange={handleChange(formik, 'memberId')}
                                onBlur={handleBlur(formik, 'memberId')}
                                aria-invalid={!!formik.errors.memberId}
                              />
                            </Box>
                            <Button
                              data-cy="submit"
                              type="submit"
                              css={styles.nextButton}
                              buttonColor={'#FFD714'}
                              textColor={'#131D2B'}
                              fontWeight={400}
                              loading={isLoading}
                            >
                              Next
                            </Button>
                          </form>
                        )
                      }}
                    </Formik>
                  </Main>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  wrapper: () => css({ width: '100%' }),
  nextButton: (theme: Theme) =>
    css({
      marginTop: '1%',
      [theme.mediaQueries.lg]: {
        position: 'relative',
      },
    }),
  toolTipButton: (theme: Theme) =>
    css({
      padding: '2px',
      borderRadius: '100px',
      backgroundColor: theme.colors.navy[400],
      height: '45px',
      width: '80px',
      fontSize: '14px',
      lineHeight: '18px',
      fontFamily: 'Source Sans Pro',
      fontWeight: 600,
    }),
  toolTipHeading: () =>
    css({
      fontFamily: 'Source Sans Pro',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '24px',
    }),
  toolTipContent: () =>
    css({
      fontFamily: 'Source Sans Pro',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '18px',
      marginTop: '5px',
    }),
  toolTipChildren: () =>
    css({
      marginTop: 10,
      width: '230px',
    }),
  paragraph: (theme: Theme) =>
    css({
      color: theme.colors.yellow[100],
      padding: '10px 0',
    }),
}
